import { createClient } from "@connectrpc/connect";
import { MultiLineString } from "geojson";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { TareaEjecucionService } from "../../../generated/grpc/rastro_tarea_ejecucion_pb";
import { wkbToMultiLineString, wkbToMultiPoint } from "../../../utils/wkbUtils";
import {
  grpcTareaEjecucionDatabase,
  StoredTareaEjecucionRecorrido,
} from "../database/GrpcTareaEjecucionDatabase";
import { TareaEjecucionRecorridoResponse } from "../response/TareaEjecucionRecorridoResponse";
import { TareaEjecucionResponse } from "../response/TareaEjecucionResponse";

/** Tope de ejecuciones por pedido: el mismo que acepta el servidor. */
const MAXIMO_POR_PEDIDO = 100;

/** Recorrido vacío, para una ejecución que el servidor guardó sin líneas. */
const SIN_LINEAS: MultiLineString = { type: "MultiLineString", coordinates: [] };

/** Recorridos de las ejecuciones pedidas, normalmente las de un día
 *  (`TareaEjecucionService.ListRecorridos`, 2026-09-30). Usa los guardados en
 *  el navegador y baja del servidor, en WKB, solo los que faltan o los de
 *  ejecuciones que cambiaron después de guardarlos. No hay watermark propio:
 *  el delta del listado de ejecuciones trae la fecha de actualización de cada
 *  una, y acá se compara con la guardada junto al recorrido. Una ejecución sin recorrido
 *  en el servidor (ajena o eliminada) no aparece en el resultado. Antes, el
 *  listado traía todos los recorridos de la historia de la finca: un cliente
 *  con un año de datos y seis GPS bajaba unos 20 MB. */
export async function fx_getTareaEjecucionRecorridosAsync(
  ejecuciones: readonly TareaEjecucionResponse[],
): Promise<Map<string, TareaEjecucionRecorridoResponse>> {
  const guardados = await grpcTareaEjecucionDatabase.getRecorridosAsync(
    ejecuciones.map((ejecucion) => ejecucion.id),
  );
  const porId = new Map(guardados.map((recorrido) => [recorrido.id, recorrido]));

  const faltantes = ejecuciones
    .filter((ejecucion) => {
      const guardado = porId.get(ejecucion.id);
      return !guardado || guardado.ultimaActualizacion < ejecucion.ultimaActualizacion;
    })
    .map((ejecucion) => ejecucion.id);

  if (faltantes.length > 0) {
    const client = createClient(TareaEjecucionService, grpcTransportProvider.get());
    for (let inicio = 0; inicio < faltantes.length; inicio += MAXIMO_POR_PEDIDO) {
      let response;
      try {
        response = await client.listRecorridos({
          ids: faltantes.slice(inicio, inicio + MAXIMO_POR_PEDIDO),
        });
      } catch (error) {
        throw mapConnectError(error);
      }

      // Copia de los bytes: los que entrega protobuf pueden ser vistas sobre la
      // respuesta entera, y guardarlas así duplicaría toda la respuesta por registro.
      const nuevos: StoredTareaEjecucionRecorrido[] = response.items.map((item) => ({
        id: item.id,
        ultimaActualizacion: Number(item.ultimaActualizacion),
        muestrasAutomaticas: new Uint8Array(item.muestrasAutomaticas),
        ejecucion: item.ejecucion ? new Uint8Array(item.ejecucion) : null,
      }));
      await grpcTareaEjecucionDatabase.putRecorridosAsync(nuevos);
      for (const recorrido of nuevos) {
        porId.set(recorrido.id, recorrido);
      }
    }
  }

  const resultado = new Map<string, TareaEjecucionRecorridoResponse>();
  for (const ejecucion of ejecuciones) {
    const recorrido = porId.get(ejecucion.id);
    if (recorrido) {
      resultado.set(ejecucion.id, toRecorridoResponse(recorrido));
    }
  }
  return resultado;
}

/** Decodifica un recorrido guardado (WKB) a GeoJSON para dibujarlo. */
function toRecorridoResponse(
  recorrido: StoredTareaEjecucionRecorrido,
): TareaEjecucionRecorridoResponse {
  return {
    id: recorrido.id,
    muestrasAutomaticas:
      recorrido.muestrasAutomaticas.length > 0
        ? wkbToMultiLineString(recorrido.muestrasAutomaticas)
        : SIN_LINEAS,
    ejecucion: recorrido.ejecucion ? wkbToMultiPoint(recorrido.ejecucion) : null,
  };
}
